import React, { useState } from "react";
import useSWR from "swr";
import { Table, Modal, Form, Input, InputNumber, message, Popconfirm, Button, Space } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  getCategoryList, createCategory, updateCategory, deleteCategory, toggleCategoryUse,
} from "../api/domainApi";
import { CategoryRes } from "../types/domain";
import PageHeader from "../components/common/PageHeader";
import UseYnTag from "../components/common/UseYnTag";

const PAGE_SIZE = 20;

const CategoryPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [kwInput, setKwInput] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CategoryRes | null>(null);
  const [form] = Form.useForm();

  const swrKey = `/category?page=${page}&kw=${keyword}`;
  const { data, isLoading, mutate } = useSWR(
    swrKey,
    () => getCategoryList({ page: page - 1, size: PAGE_SIZE, keyword }),
    { keepPreviousData: true }
  );

  const openCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (row: CategoryRes) => {
    setEditing(row);
    form.setFieldsValue(row);
    setModalOpen(true);
  };

  const handleOk = async () => {
    const values = await form.validateFields();
    try {
      if (editing) {
        await updateCategory(editing.id, values);
        message.success("수정되었습니다.");
      } else {
        await createCategory(values);
        message.success("추가되었습니다.");
      }
      setModalOpen(false);
      mutate();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteCategory(id);
      message.success("삭제되었습니다.");
      mutate();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const columns: ColumnsType<CategoryRes> = [
    { title: "ID", dataIndex: "id", width: 70 },
    { title: "코드", dataIndex: "code", width: 100 },
    { title: "카테고리명", dataIndex: "codeName", ellipsis: { showTitle: true } },
    { title: "API URL", dataIndex: "apiUrl", ellipsis: { showTitle: true } },
    { title: "파일그룹ID", dataIndex: "fileGrpId", width: 100 },
    { title: "순서", dataIndex: "sortOrder", width: 60 },
    { title: "사용여부", dataIndex: "useYn", width: 90, render: (v) => <UseYnTag value={v} /> },
    {
      title: "관리",
      width: 180,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>수정</Button>
          <Button size="small" onClick={async () => { try { await toggleCategoryUse(row.id); mutate(); } catch (e: any) { message.error(e.message); } }}>
            {row.useYn ? "비활성" : "활성"}
          </Button>
          <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(row.id)}>
            <Button size="small" danger>삭제</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="카테고리" onAdd={openCreate} />
      <Space style={{ marginBottom: 16 }}>
        <Input placeholder="카테고리명 검색" value={kwInput}
          onChange={(e) => setKwInput(e.target.value)}
          onPressEnter={() => { setKeyword(kwInput); setPage(1); }}
          style={{ width: 220 }}
        />
        <Button onClick={() => { setKeyword(kwInput); setPage(1); }}>검색</Button>
        <Button onClick={() => { setKwInput(""); setKeyword(""); setPage(1); }}>초기화</Button>
      </Space>
      <Table
        rowKey="id" columns={columns} dataSource={data?.content} loading={isLoading}
        pagination={{ current: page, pageSize: PAGE_SIZE, total: data?.page?.totalElements, onChange: setPage, showSizeChanger: false }}
        size="small"
      />
      <Modal
        title={editing ? "카테고리 수정" : "카테고리 추가"}
        open={modalOpen} onOk={handleOk} onCancel={() => setModalOpen(false)}
        okText={editing ? "수정" : "추가"} cancelText="취소"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="code" label="카테고리 코드" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="codeName" label="카테고리명" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="설명"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="fileGrpId" label="파일 그룹 ID" rules={[{ required: true }]}>
            <InputNumber style={{ width: "100%" }} min={1} />
          </Form.Item>
          <Form.Item name="apiUrl" label="API URL" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="sortOrder" label="정렬 순서" rules={[{ required: true }]}>
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoryPage;
